/* eslint-disable @stripe-internal/embedded/no-restricted-globals */
import {isConnectElementTag} from '../connect/utils/connectElementTags';
import type {IUIReadjustIframe} from './UILayerMessages';
import {UILayerWrapper} from './UILayerWrapper';
import {
  embeddedComponentParamName,
  pageViewIdParamName,
} from './buildUILayerFrame';
import {initIframeErrorReporter} from './utils/IframeErrorReporter';
import {CONNECT_ELEMENT_IMPORTS} from '../connect/ConnectJSInterface/ConnectElementList';
import {setCurrentPageViewId} from '../data-layer-client/Metrics';

// This frame expects to receive the embedded component to render at load time via a hash param.
const searchParams = new URLSearchParams(location.hash.replace('#', ''));
const embeddedComponent = searchParams.get(embeddedComponentParamName);
const pageViewId = searchParams.get(pageViewIdParamName);
if (pageViewId) {
  setCurrentPageViewId(pageViewId);
}

if (!embeddedComponent) {
  throw new Error('Missing embedded component query param');
}
if (!isConnectElementTag(embeddedComponent)) {
  throw new Error(`Invalid embedded component ${embeddedComponent}`);
}

const connectElementConfig = CONNECT_ELEMENT_IMPORTS[embeddedComponent];

const errorReporter = initIframeErrorReporter({
  connectElement: embeddedComponent,
  iframeLayer: 'ui-layer',
});

// Disable min width
const shouldDisableMinWidth = !!connectElementConfig.disableMinWidth;

// Set styles on the UI layer since we do not want to add an `unsafe-inline` directive for the `style-src` CSP policy
document.body.style.margin = '0px';
document.body.style.padding = '0px';
document.body.style.overflow = 'hidden';
const rootElement = document.getElementById('root');
if (!rootElement) {
  const error = new Error('Missing root element in UI layer!');
  errorReporter.captureIframeException(error);
  throw error;
}
rootElement.style.margin = '0px';
rootElement.style.padding = '4px'; // This extra padding on the root element is used for the focusRing which is 4px to accommodate for the focus ring if it's placed at the boundaries of the UI layer frame
rootElement.style.overflow = 'hidden';

const uiLayer = new UILayerWrapper(embeddedComponent);

const uiLayerResizeObserver = new ResizeObserver((entries) => {
  for (let i = 0; i < entries.length; i += 1) {
    const entry = entries[i];
    const values: IUIReadjustIframe['values'] = {
      id: window.name,
      resize: {
        styles: {
          // We add a negative margin as we have an extra padding 4px on the root element in the UI layer frame
          // This 4px accounts for the focus ring should a button be placed at the boundaries of the UI layer frame.
          // We also have to increase the width to account for the negative margin.
          width: shouldDisableMinWidth
            ? 'calc(100% + 8px)'
            : 'calc(max(100%, 320px) + 8px)',
          height: getComputedStyle(entry.target).height,
          margin: '-4px',
        },
      },
    };

    if (!uiLayer.uiLayerMessagePort) {
      const error = new Error('Missing port');
      errorReporter.captureIframeException(error);
      throw error;
    }
    uiLayer.uiLayerMessagePort.readjustIframe(values);
  }
});

// Create an observer instance linked to the callback function
const uiLayermutationObserver = new MutationObserver(
  (mutationList: MutationRecord[]) => {
    mutationList.forEach((mutation) => {
      if (mutation.type === 'childList') {
        const addedElement = rootElement.firstElementChild;
        if (addedElement) {
          uiLayerResizeObserver.observe(rootElement);
          uiLayermutationObserver.disconnect();
        }
      }
    });
  },
);

// Start observing the target node for configured mutations
uiLayermutationObserver.observe(rootElement, {
  childList: true,
});

uiLayer.initUILayer();
